import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react"
import PPUtils from "../utils/PPUtils"

const authContext = createContext()

export function ProvideAuth({ children }) {
    const auth = useProvideAuth()
    return <authContext.Provider value={auth}>{children}</authContext.Provider>
}

export const useAuth = () => {
    return useContext(authContext)
}

export function isLoggedIn(login) {
    if (login == null || login === false || typeof login !== "object") {
        return false
    }
    return typeof login.token === "string" && login.token !== ""
}

function useProvideAuth() {
    const [login, setLogin] = useState(PPUtils.getLogin())
    const [failed, setFailed] = useState(false)

    useEffect(() => {
        if (login) {
            try {
                let json = JSON.stringify(login)
                if (localStorage.getItem("login")) {
                    localStorage.setItem("login", json)
                } else if (sessionStorage.getItem("login")) {
                    sessionStorage.setItem("login", json)
                }
            } catch {
                // localStorage/sessionStorage blocked (e.g. cross-origin iframe)
            }
        }
    }, [login])

    useEffect(() => {
        if (typeof window === "undefined") {
            return undefined
        }
        const refreshLogin = () => {
            const nextLogin = PPUtils.getLogin()
            if (nextLogin) {
                setLogin(nextLogin)
            }
        }
        window.addEventListener("pp-user-settings-updated", refreshLogin)
        return () => window.removeEventListener("pp-user-settings-updated", refreshLogin)
    }, [])

    const signin = useCallback((username, password, remember) => {
        setFailed(false)
        return fetch("/login", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ username, password })
        })
            .then(async (response) => {
                if (response.status === 401) {
                    setFailed(true)
                    return
                }
                if (!response.ok) {
                    throw new Error(response.statusText)
                }
                const data = await response.json()
                const hasToken = data != null && typeof data.token === "string" && data.token !== ""
                if (hasToken) {
                    try {
                        if (remember) {
                            sessionStorage.removeItem("login")
                            localStorage.setItem("login", JSON.stringify(data))
                        } else {
                            localStorage.removeItem("login")
                            sessionStorage.setItem("login", JSON.stringify(data))
                        }
                    } catch {
                        // Speicher nicht verfügbar (z. B. privater Modus): Login gilt dann nur im State dieser Sitzung.
                    }
                    setLogin(data)
                    setFailed(false)
                    return data
                }
                setFailed(true)
            })
            .catch(function (error) {
                console.error(error)
            })
    }, [])

    const signout = useCallback(() => {
        PPUtils.removeLogin()
        setLogin(null)
    }, [])

    const isExpired = useCallback(() => {
        return PPUtils.isTokenExpired(login || PPUtils.getLogin())
    }, [login])

    const isAdmin = useCallback(() => {
        const loginData = PPUtils.getLogin()
        if (!loginData?.user) return false
        if (loginData.user.rights == "admin" && loginData.customerUsers?.length > 1) {
            if (loginData.token_rights == "user") return false
        }
        return loginData.user.rights === "admin"
    }, [])

    const hasProviderSummaryAccess = useCallback(() => {
        const loginData = PPUtils.getLogin()
        if (!loginData?.user) return false
        return (
            loginData.user.rights == "admin" ||
            loginData.user.providerSummaryAccess === "1" ||
            loginData.user.providerSummaryAccess === true
        )
    }, [])

    const hasPpAiAccess = useCallback(() => {
        const loginData = PPUtils.getLogin()
        return !!loginData?.mcpServerToken
    }, [])

    return useMemo(
        () => ({
            login,
            failed,
            isExpired,
            signin,
            signout,
            isAdmin,
            hasProviderSummaryAccess,
            hasPpAiAccess,
            setLogin
        }),
        [login, failed, isExpired, signin, signout, isAdmin, hasProviderSummaryAccess, hasPpAiAccess]
    )
}
